import { useRef, useState, type FormEvent, type DragEvent } from "react";

type Status = "idle" | "success" | "error";

const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const RECIPIENT = "info@elektro-zweering.de";

export default function BewerbungForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function addFiles(newFiles: FileList | File[]) {
    const arr = Array.from(newFiles);
    const oversized = arr.find((f) => f.size > MAX_SIZE);
    if (oversized) {
      setStatus("error");
      setErrorMsg(`Datei "${oversized.name}" ist größer als 10 MB.`);
      return;
    }
    setFiles((prev) => [...prev, ...arr]);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("idle");
    setErrorMsg("");

    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") || "").trim();
    const email = String(data.get("email") || "").trim();
    const phone = String(data.get("phone") || "").trim();
    const art = String(data.get("art") || "").trim();
    const beschreibung = String(data.get("beschreibung") || "").trim();
    const datenschutz = data.get("datenschutz") === "on";

    if (!name || !email || !phone || !art || !beschreibung || !datenschutz) {
      setStatus("error");
      setErrorMsg("Bitte prüfe deine Angaben.");
      return;
    }
    if (beschreibung.length < 50) {
      setStatus("error");
      setErrorMsg("Bitte beschreibe dich in mindestens 50 Zeichen.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error");
      setErrorMsg("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }

    const fileList = files.length
      ? `\n\nAnhänge (bitte separat per Mail nachreichen):\n${files
          .map((f) => `- ${f.name} (${(f.size / 1024).toFixed(0)} KB)`)
          .join("\n")}`
      : "";

    const subject = `Bewerbung: ${art} — ${name}`;
    const body =
      `Name: ${name}\n` +
      `E-Mail: ${email}\n` +
      `Telefon: ${phone}\n` +
      `Art der Bewerbung: ${art}\n\n` +
      `Beschreibung:\n${beschreibung}` +
      fileList;

    const mailto = `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    setStatus("success");
    form.reset();
    setFiles([]);
  }

  const inputClass =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#0A0A0A]/10 text-[#0A0A0A] text-base focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition";

  return (
    <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14">
      <div className="max-w-4xl mx-auto">
        <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
          BEWERBUNG
        </p>
        <h2
          className="font-bold uppercase text-[#0A0A0A] leading-[0.95] break-words"
          style={{
            fontSize: "clamp(1.5rem, 5vw, 3.5rem)",
            letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
          }}
        >
          SCHICK UNS DEINE BEWERBUNG
        </h2>
        <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl">
          Wir melden uns innerhalb weniger Tage zurück — kurzer Weg, echtes Gespräch.
        </p>

        <form onSubmit={handleSubmit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5" noValidate>
          <div>
            <label htmlFor="bw-name" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Name *
            </label>
            <input id="bw-name" name="name" type="text" required maxLength={100} className={inputClass} />
          </div>
          <div>
            <label htmlFor="bw-email" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              E-Mail *
            </label>
            <input id="bw-email" name="email" type="email" required maxLength={255} className={inputClass} />
          </div>
          <div>
            <label htmlFor="bw-phone" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Telefon *
            </label>
            <input id="bw-phone" name="phone" type="tel" required maxLength={50} className={inputClass} />
          </div>
          <div>
            <label htmlFor="bw-art" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Art der Bewerbung *
            </label>
            <select id="bw-art" name="art" required defaultValue="" className={inputClass}>
              <option value="" disabled>
                Bitte auswählen
              </option>
              <option value="Festanstellung">Festanstellung</option>
              <option value="Ausbildungsplatz">Ausbildungsplatz</option>
              <option value="Praktikumsplatz">Praktikumsplatz</option>
              <option value="Initiativbewerbung">Initiativbewerbung</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="bw-beschreibung" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Kurze Beschreibung zu deiner Person *
            </label>
            <textarea
              id="bw-beschreibung"
              name="beschreibung"
              required
              minLength={50}
              maxLength={2000}
              rows={6}
              className={inputClass}
              placeholder="Erzähl uns kurz von dir — Erfahrung, Motivation, was dich interessiert. (mind. 50 Zeichen)"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="bw-file" className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Lebenslauf / Unterlagen (optional)
            </label>
            <div
              role="button"
              tabIndex={0}
              aria-label="Dateien auswählen — Lebenslauf oder Unterlagen hochladen"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
                dragOver
                  ? "border-[#4CAF50] bg-[#4CAF50]/5"
                  : "border-[#0A0A0A]/15 bg-[#F4F4F4] hover:border-[#4CAF50]/50"
              }`}
            >
              <p className="text-foreground-600 text-sm">
                Dateien hierher ziehen oder{" "}
                <span className="text-[#4CAF50] font-medium underline">durchsuchen</span>
                <br />
                <span className="text-xs text-foreground-500">PDF, DOC, Bilder — max. 10 MB pro Datei</span>
              </p>
              <input
                id="bw-file"
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
            </div>
            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between bg-[#F4F4F4] rounded-lg px-4 py-2 text-sm"
                  >
                    <span className="truncate text-[#0A0A0A]">
                      {f.name}{" "}
                      <span className="text-foreground-500">({(f.size / 1024).toFixed(0)} KB)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-foreground-500 hover:text-[#0A0A0A] ml-3"
                      aria-label={`${f.name} entfernen`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="datenschutz"
                required
                className="mt-1 h-4 w-4 accent-[#4CAF50]"
              />
              <span className="text-sm text-foreground-600 leading-relaxed">
                Ich habe die{" "}
                <a
                  href="/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4CAF50] underline hover:no-underline"
                >
                  Datenschutzerklärung
                </a>{" "}
                gelesen und stimme der Verarbeitung meiner Daten zu. *
              </span>
            </label>
          </div>

          {status === "error" && (
            <div className="md:col-span-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMsg || "Bitte prüfe deine Angaben."}
            </div>
          )}
          {status === "success" && (
            <div className="md:col-span-2 rounded-xl border border-[#4CAF50]/40 bg-[#4CAF50]/10 px-4 py-3 text-sm text-[#0A0A0A]">
              Vielen Dank! Wir haben deine Bewerbung erhalten und melden uns innerhalb weniger Tage.
            </div>
          )}

          <div className="md:col-span-2">
            <button
              type="submit"
              className="btn-slide btn-fill-white inline-flex items-center px-8 py-4 rounded-full text-sm font-medium transition-all bg-[#4CAF50] text-white"
            >
              Bewerbung senden →
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
